/**
 * MKS-372 — "Create role rates from current assignment rates".
 *
 * Rendered inside the project form's existing type-change confirmation when
 * that change moves the project to Time & Material. It does not replace the
 * confirmation: the type-change lines and the "already-invoiced months are
 * untouched" note stay, and this is the step above the Save button.
 *
 * Three things:
 *
 *   - the failing checklist, straight from `checkTmConversionReadiness`, each
 *     item linking to the project's Team Assignments so the user can go and
 *     look at the people it names;
 *   - a picker per role whose people disagree about the rate, showing who
 *     would move from → to;
 *   - an inline role picker per person with no role.
 *
 * Pressing the button writes the picks through
 * `POST /projects/:id/tm-conversion-seed` — role rates and assignment roles in
 * one transaction. It does NOT convert the project; the form's own save still
 * does that, which is why `onSeeded` hands the written rates back for the
 * payload instead of this component touching the form's state itself.
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import {
  useTmConversionReadiness,
  useTmConversionSeedOptions,
  useApplyTmConversionSeed,
} from '../../hooks/useTmConversion';
import { useProjectRoles } from '../../hooks/useLeads';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { TmReadinessIssue } from '../../services/api/tmConversion';

interface Props {
  projectId: number;
  /** The rates the form should send on save, once they have been created. */
  onSeeded: (rates: Array<{ roleId: string; hourlyRate: string }>) => void;
  /** Lets the form disable its Save button while the data is not ready. */
  onReadyChange?: (ready: boolean) => void;
}

function issueTitle(issue: TmReadinessIssue): string {
  switch (issue.type) {
    case 'assignment_without_role':
      return 'People without a role';
    case 'role_without_rate':
      return `No rate for ${issue.roleName ?? 'a role'}`;
    case 'role_rate_conflict':
      return `Two rates for ${issue.roleName ?? 'a role'}`;
    case 'currency_mismatch':
      return `Wrong currency for ${issue.roleName ?? 'a role'}`;
    default:
      return 'Not ready';
  }
}

export function TmConversionStep({ projectId, onSeeded, onReadyChange }: Props) {
  const readinessQuery = useTmConversionReadiness(projectId, true);
  const seedQuery = useTmConversionSeedOptions(projectId, true);
  const { data: rolesData } = useProjectRoles();
  const seedMutation = useApplyTmConversionSeed(projectId);

  // roleId -> the rate string the user picked (or typed).
  const [ratePicks, setRatePicks] = useState<Record<number, string>>({});
  // assignmentId -> the role the user picked for a person who has none.
  const [rolePicks, setRolePicks] = useState<Record<number, number | ''>>({});
  const [error, setError] = useState<string | null>(null);

  const options = seedQuery.data;
  const readiness = readinessQuery.data;

  // Preselect the rate each role's majority already carries, so a project
  // where nobody disagrees is one button press with nothing to decide.
  useEffect(() => {
    if (!options) return;
    setRatePicks((prev) => {
      const next = { ...prev };
      for (const role of options.roles) {
        if (next[role.roleId] !== undefined) continue;
        const suggested = role.currentRate ?? role.candidates[0]?.billingRate ?? '';
        if (suggested) next[role.roleId] = suggested;
      }
      return next;
    });
  }, [options]);

  useEffect(() => {
    if (readiness) onReadyChange?.(readiness.ready);
  }, [readiness, onReadyChange]);

  const allRoles = rolesData?.data ?? [];

  const unresolvedPeople = useMemo(
    () => (options?.rolelessPeople ?? []).filter((p) => !rolePicks[p.assignmentId]),
    [options, rolePicks],
  );

  const handleSeed = async () => {
    if (!options) return;
    setError(null);
    const roleRates = options.roles
      .map((role) => ({ roleId: role.roleId, hourlyRate: (ratePicks[role.roleId] ?? '').trim() }))
      .filter((r) => r.hourlyRate !== '' && Number(r.hourlyRate) > 0);
    const assignmentRoles = Object.entries(rolePicks)
      .filter(([, roleId]) => roleId !== '')
      .map(([assignmentId, roleId]) => ({
        assignmentId: Number(assignmentId),
        roleId: roleId as number,
      }));
    if (roleRates.length === 0 && assignmentRoles.length === 0) {
      setError('Pick a rate or a role first — there is nothing to create yet.');
      return;
    }
    try {
      await seedMutation.mutateAsync({ roleRates, assignmentRoles });
      onSeeded(
        roleRates.map((r) => ({ roleId: String(r.roleId), hourlyRate: r.hourlyRate })),
      );
      // A person who has just been given a role may bring a new role with no
      // rate, so the refreshed readiness decides whether we are done — this
      // component does not assume the write fixed everything.
      setRolePicks({});
    } catch (err) {
      setError(toErrorMessage(err, 'Could not create the role rates.'));
    }
  };

  if (readinessQuery.isLoading || seedQuery.isLoading) {
    return (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <Spinner />
      </div>
    );
  }

  if (readinessQuery.isError || seedQuery.isError) {
    return (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <p className="text-sm text-red-800">
          {toErrorMessage(
            readinessQuery.error ?? seedQuery.error,
            'Could not check whether this project is ready for Time &amp; Material.',
          )}
        </p>
      </div>
    );
  }

  const issues = readiness?.issues ?? [];

  return (
    <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">
          Create role rates from current assignment rates
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          On Time &amp; Material the rate of a role is the rate of everyone in it. These
          are the rates your people carry today.
        </p>
      </div>

      {issues.length === 0 ? (
        <p className="text-sm text-green-800">
          Every person has a role, every role has one rate, and the currencies agree. This
          project is ready.
        </p>
      ) : (
        <ul className="space-y-2">
          {issues.map((issue, idx) => (
            <li
              key={`${issue.type}-${issue.roleId ?? 'none'}-${idx}`}
              className="rounded border border-amber-200 bg-amber-50 p-3"
            >
              <p className="text-sm font-medium text-amber-900">{issueTitle(issue)}</p>
              <p className="mt-1 text-sm text-amber-800">{issue.message}</p>
              {issue.people.length > 0 && (
                <p className="mt-1 text-xs text-amber-800">
                  {issue.people
                    .map(
                      (p) =>
                        `${p.userName ?? `#${p.userId ?? p.assignmentId}`}` +
                        (p.billingRate
                          ? ` (${p.billingRate} ${p.billingRateCurrency})`
                          : ' (no rate)'),
                    )
                    .join(', ')}
                </p>
              )}
              <Link
                to={`/projects/${projectId}`}
                className="mt-1 inline-block text-xs text-blue-700 underline"
              >
                Open Team Assignments
              </Link>
            </li>
          ))}
        </ul>
      )}

      {(options?.roles ?? []).length > 0 && (
        <div className="space-y-3">
          {options!.roles.map((role) => (
            <div key={role.roleId} className="rounded border border-gray-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-gray-900">
                  {role.roleName ?? `Role #${role.roleId}`}
                </span>
                <span className="text-xs text-gray-500">
                  {options!.tmBillingCurrency}
                </span>
              </div>
              {role.candidates.length > 1 ? (
                <div className="mt-2 space-y-1">
                  {role.candidates.map((candidate) => {
                    const value = candidate.billingRate ?? '';
                    return (
                      <label
                        key={`${role.roleId}-${value}-${candidate.billingRateCurrency}`}
                        className="flex items-start gap-2 text-sm text-gray-800"
                      >
                        <input
                          type="radio"
                          name={`tm-rate-${role.roleId}`}
                          className="mt-1"
                          checked={(ratePicks[role.roleId] ?? '') === value}
                          onChange={() =>
                            setRatePicks((prev) => ({ ...prev, [role.roleId]: value }))
                          }
                        />
                        <span>
                          <span className="font-medium">
                            {candidate.billingRate ?? 'no rate'}{' '}
                            {candidate.billingRateCurrency}
                          </span>
                          <span className="block text-xs text-gray-600">
                            {candidate.people
                              .map((p) => p.userName ?? `#${p.userId ?? p.assignmentId}`)
                              .join(', ')}
                            {' — '}
                            {candidate.billingRate === (ratePicks[role.roleId] ?? '')
                              ? 'keeps this rate'
                              : `${candidate.billingRate ?? 'no rate'} → ${
                                  ratePicks[role.roleId] || '—'
                                }`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`Hourly rate for ${role.roleName ?? `role ${role.roleId}`}`}
                    value={ratePicks[role.roleId] ?? ''}
                    onChange={(e) =>
                      setRatePicks((prev) => ({ ...prev, [role.roleId]: e.target.value }))
                    }
                    className="w-32 rounded border border-gray-300 px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-gray-600">
                    {role.candidates[0]?.people.length ?? 0} person(s) in this role
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(options?.rolelessPeople ?? []).length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-900">Give these people a role</p>
          {options!.rolelessPeople.map((person) => (
            <div key={person.assignmentId} className="flex items-center gap-2">
              <span className="w-48 truncate text-sm text-gray-800">
                {person.userName ?? `#${person.userId ?? person.assignmentId}`}
              </span>
              <select
                aria-label={`Role for ${person.userName ?? `assignment ${person.assignmentId}`}`}
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={rolePicks[person.assignmentId] ?? ''}
                onChange={(e) =>
                  setRolePicks((prev) => ({
                    ...prev,
                    [person.assignmentId]: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
              >
                <option value="">Pick a role…</option>
                {allRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-600">
                {person.billingRate
                  ? `${person.billingRate} ${person.billingRateCurrency}`
                  : 'no rate'}
              </span>
            </div>
          ))}
          {unresolvedPeople.length > 0 && (
            <p className="text-xs text-gray-600">
              {unresolvedPeople.length} person(s) still have no role.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-800">{error}</p>}

      <Button
        variant="secondary"
        size="sm"
        disabled={seedMutation.isPending}
        onClick={() => void handleSeed()}
      >
        {seedMutation.isPending ? 'Creating…' : 'Create role rates'}
      </Button>
    </div>
  );
}

export default TmConversionStep;
